import { useCallback, useMemo, useRef, useState } from 'react';

import { normalizeSkillName, parseSkillMarkdown } from '@aline/common';
import type { SkillFrontmatter } from '@aline/common';
import { useSnackbar } from 'notistack';
import { useLatest } from 'react-use';
import { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';
import { getHasSkillAttachmentChanges } from './skill-autosave-changes';
import {
  getSkillContentError,
  getSkillContentIsSaved,
  getSkillContentWithFinalName,
  setSkillContentBody,
  setSkillContentFrontmatter,
} from './skill-editor-content';
import { getEditorSkill, getSkillEditorKey } from './skill-editor-draft';
import { getSectionAfterSave } from './skill-file-rules';
import type { SkillMarkdownEditorRef } from './skill-markdown-editor';
import type { SkillSaveError } from './skill-save-error';
import type { SkillTextFileEditorRef } from './skill-text-file-editor';
import type { SkillTreeItem } from './skill-tree-ids';
import { useSkillSaveQueue } from './use-skill-autosave';
import type { SaveAttempt } from './use-skill-autosave';
import { useSkillRemoteSync } from './use-skill-remote-sync';
import { useStateRef } from './use-state-ref';

/** An action that waits for the save of everything typed before it was asked for. */
export type PendingAction = 'run' | 'back' | 'duplicate' | 'editWithAi' | 'publish';

/**
 * What the editor holds of the skill, as refs and stable writers. Everything a save reads
 * lives in refs as well as state, written together: a save that runs after the editor
 * unmounted (the last keystrokes, or a save queued behind one in flight) still sees the
 * latest edits, and an edit never waits for a render to be seen.
 */
export interface SkillDraftStore {
  attachmentChangesRef: React.RefObject<SkillAttachmentChanges>;
  canEditRef: React.RefObject<boolean>;
  contentRef: React.RefObject<string>;
  /** Set once Delete starts: no save may start after it. */
  deletingRef: React.RefObject<boolean>;
  /** Bumped when the editor loads a version, so the body editor starts over from it. */
  editorGenerationRef: React.RefObject<number>;
  editRevisionRef: React.RefObject<number>;
  flushEditors: () => void;
  getHasUnsavedChanges: () => boolean;
  /** The key of the version the editor loaded. A different key seen in the cache came from elsewhere. */
  initializedKeyRef: React.RefObject<string | null>;
  reportSaveError: (error: SkillSaveError) => void;
  saveAttemptRef: React.RefObject<SaveAttempt | null>;
  savedContentRef: React.RefObject<string | null>;
  savedPathsRef: React.RefObject<string[]>;
  saveErrorRef: React.RefObject<SkillSaveError | null>;
  setSection: React.Dispatch<React.SetStateAction<SkillTreeItem>>;
  skillRef: React.RefObject<Apollo.AgentSkillSimpleFragment | null>;
  writeAttachmentChanges: (next: SkillAttachmentChanges) => void;
  writeContent: (next: string) => void;
  writeSavedContent: (next: string) => void;
  writeSaveError: (next: SkillSaveError | null) => void;
  writeSkill: (next: Apollo.AgentSkillSimpleFragment) => void;
}

export type SkillDraftSession = ReturnType<typeof useSkillDraftSession>;

/**
 * The skill being edited and the editor's unsaved work on it: loading, editing, saving, and
 * taking in changes made elsewhere.
 */
export function useSkillDraftSession({
  getIsEditorMounted,
  setExpandedFolders,
  setSection,
  skillId,
}: {
  getIsEditorMounted: () => boolean;
  setExpandedFolders: React.Dispatch<React.SetStateAction<string[]>>;
  setSection: React.Dispatch<React.SetStateAction<SkillTreeItem>>;
  skillId: string;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    data,
    error: queryError,
    loading: queryLoading,
    refetch: refetchSkill,
  } = Apollo.useGetAgentSkillQuery({ fetchPolicy: 'network-only', variables: { id: skillId } });

  const lastEditAtRef = useRef(0);
  const deletingRef = useRef(false);
  const savedPathsRef = useRef<string[]>([]);
  const initializedKeyRef = useRef<string | null>(null);
  const saveAttemptRef = useRef<SaveAttempt | null>(null);
  const bodyEditorRef = useRef<SkillMarkdownEditorRef>(null);
  const [content, contentRef, writeContent] = useStateRef('');
  const textFileEditorRef = useRef<SkillTextFileEditorRef>(null);
  const [editRevision, editRevisionRef, writeEditRevision] = useStateRef(0);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [skill, skillRef, writeSkill] = useStateRef<Apollo.AgentSkillSimpleFragment | null>(null);
  const [savedContent, savedContentRef, writeSavedContent] = useStateRef<string | null>(null);
  const [saveError, saveErrorRef, writeSaveError] = useStateRef<SkillSaveError | null>(null);
  const [attachmentChanges, attachmentChangesRef, writeAttachmentChanges] =
    useStateRef<SkillAttachmentChanges>({});
  const [editorGeneration, editorGenerationRef, writeEditorGeneration] = useStateRef(0);
  const canEdit = Boolean(skill?.canEdit);
  const canEditRef = useLatest(canEdit);

  const getHasUnsavedChanges = useCallback(() => {
    const saved = savedContentRef.current;
    if (!canEditRef.current || saved === null) return false;

    return (
      !getSkillContentIsSaved(contentRef.current, saved) ||
      getHasSkillAttachmentChanges(attachmentChangesRef.current, savedPathsRef.current)
    );
  }, [attachmentChangesRef, canEditRef, contentRef, savedContentRef]);

  /** Report edits still waiting in an editor's debounce, so what follows reads them. */
  const flushEditors = useCallback(() => {
    if (!canEditRef.current) return;
    bodyEditorRef.current?.flush();
    textFileEditorRef.current?.flush();
  }, [canEditRef]);

  const reportSaveError = useCallback(
    (error: SkillSaveError) => {
      writeSaveError(error);
      setPendingAction(null);
      // A failure after the editor is gone would otherwise go unseen.
      if (!getIsEditorMounted())
        enqueueSnackbar(`Your last changes to the skill were not saved. ${error.message}`, {
          variant: 'error',
        });
    },
    [enqueueSnackbar, getIsEditorMounted, writeSaveError]
  );

  const store: SkillDraftStore = {
    attachmentChangesRef,
    canEditRef,
    contentRef,
    deletingRef,
    editRevisionRef,
    editorGenerationRef,
    flushEditors,
    getHasUnsavedChanges,
    initializedKeyRef,
    reportSaveError,
    saveAttemptRef,
    saveErrorRef,
    savedContentRef,
    savedPathsRef,
    setSection,
    skillRef,
    writeAttachmentChanges,
    writeContent,
    writeSaveError,
    writeSavedContent,
    writeSkill,
  };

  const parsedContent = useMemo(() => parseSkillMarkdown(content), [content]);
  const savedPaths = useMemo(() => skill?.attachments.map(file => file.path) ?? [], [skill]);
  const dirty =
    canEdit &&
    savedContent !== null &&
    (!getSkillContentIsSaved(content, savedContent) ||
      getHasSkillAttachmentChanges(attachmentChanges, savedPaths));
  // Checked as it will be saved, so a hyphen still being typed is not an error.
  const contentError = getSkillContentError(getSkillContentWithFinalName(content));

  const { activeRef, handleEditorKeyDown, requestAutosave, requestSave, saveQueue, saving } =
    useSkillSaveQueue({ skillId, store });

  /** Clear a failed save's error so the next save runs, unless it was a conflict. */
  const clearRetryableSaveError = useCallback(() => {
    if (saveErrorRef.current && !saveErrorRef.current.outdated) writeSaveError(null);
  }, [saveErrorRef, writeSaveError]);
  const afterEdit = useCallback(() => {
    writeEditRevision(editRevisionRef.current + 1);
    lastEditAtRef.current = Date.now();
    clearRetryableSaveError();
    // An editor torn down with the page reports its last keystrokes after the page's final save.
    if (!activeRef.current) void requestSave();
  }, [activeRef, clearRetryableSaveError, editRevisionRef, requestSave, writeEditRevision]);
  const editContent = useCallback(
    (next: string) => {
      if (next === contentRef.current) return;
      writeContent(next);
      afterEdit();
    },
    [afterEdit, contentRef, writeContent]
  );
  const editAttachmentChanges = useCallback(
    (update: React.SetStateAction<SkillAttachmentChanges>) => {
      const next = typeof update === 'function' ? update(attachmentChangesRef.current) : update;
      if (next === attachmentChangesRef.current) return;
      writeAttachmentChanges(next);
      afterEdit();
    },
    [afterEdit, attachmentChangesRef, writeAttachmentChanges]
  );

  /** Start the editor over from a version fetched from the server. */
  const initializeEditor = useCallback(
    (fetched: Apollo.AgentSkillSimpleFragment) => {
      const loaded = getEditorSkill(fetched);
      writeSkill(loaded);
      saveAttemptRef.current = null;
      initializedKeyRef.current = getSkillEditorKey(loaded);
      writeEditorGeneration(editorGenerationRef.current + 1);
      writeContent(loaded.content);
      writeSavedContent(loaded.content);
      savedPathsRef.current = loaded.attachments.map(file => file.path);
      writeAttachmentChanges({});
      setSection(current =>
        getSectionAfterSave({
          attachments: loaded.attachments,
          current,
          remaining: {},
          submitted: {},
        })
      );
      writeSaveError(null);
    },
    [
      editorGenerationRef,
      setSection,
      writeAttachmentChanges,
      writeContent,
      writeEditorGeneration,
      writeSaveError,
      writeSavedContent,
      writeSkill,
    ]
  );

  const { observedKey, reloadSkill, reloading, retryLoad } = useSkillRemoteSync({
    canEdit,
    initializeEditor,
    observedSkill: data?.agentSkill,
    refetchSkill,
    saveQueue,
    saving,
    setExpandedFolders,
    setPendingAction,
    skill,
    skillId,
    store,
  });
  const reloadSkillClick = useCallback(() => void reloadSkill(), [reloadSkill]);
  const retrySave = useCallback(() => {
    writeSaveError(null);
    void requestSave();
  }, [requestSave, writeSaveError]);

  const handleBodyChange = useCallback(
    (markdown: string) => {
      // An editor replaced by a newer version may still flush a change made to the old one.
      if (!canEdit || editorGeneration !== editorGenerationRef.current) return;
      editContent(setSkillContentBody(contentRef.current, markdown));
    },
    [canEdit, contentRef, editContent, editorGeneration, editorGenerationRef]
  );
  const editFrontmatter = useCallback(
    (update: (frontmatter: SkillFrontmatter) => SkillFrontmatter) => {
      if (!canEdit) return;
      const parsed = parseSkillMarkdown(contentRef.current);
      if (parsed.ok)
        editContent(setSkillContentFrontmatter(contentRef.current, update(parsed.frontmatter)));
    },
    [canEdit, contentRef, editContent]
  );
  const handleFrontmatterChange = useCallback(
    (frontmatter: SkillFrontmatter) => editFrontmatter(() => frontmatter),
    [editFrontmatter]
  );
  const handleTitleSave = useCallback(
    (name: string) =>
      editFrontmatter(frontmatter => ({ ...frontmatter, name: normalizeSkillName(name) })),
    [editFrontmatter]
  );

  return {
    attachmentChanges,
    attachmentChangesRef,
    bodyEditorRef,
    canEdit,
    clearRetryableSaveError,
    contentError,
    contentRef,
    deletingRef,
    dirty,
    editAttachmentChanges,
    editRevision,
    editorGeneration,
    flushEditors,
    getHasUnsavedChanges,
    handleBodyChange,
    handleEditorKeyDown,
    handleFrontmatterChange,
    handleTitleSave,
    initializeEditor,
    initializedKeyRef,
    lastEditAtRef,
    observedKey,
    parsedContent,
    pendingAction,
    queryError,
    queryLoading,
    reloadSkillClick,
    reloading,
    reportSaveError,
    requestAutosave,
    requestSave,
    retryLoad,
    retrySave,
    saveError,
    saveErrorRef,
    saveQueue,
    savedPaths,
    saving,
    setPendingAction,
    skill,
    skillRef,
    textFileEditorRef,
    writeSaveError,
    writeSkill,
  };
}
